import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, Outlet, useParams } from "react-router";
import { useAuth } from "@/modules/auth";
import { useBrands } from "@/modules/brand";
import { brandApi } from "@/modules/brand/services/brand-api";
import { usePageLoader } from "@/contexts/page-loader-context";

type AccessError = "not_found" | "access_denied" | null;

function WorkspaceErrorDisplay() {
	const navigate = useNavigate();

	return (
		<div className="flex items-center justify-center min-h-[calc(100vh-4rem)] bg-[#fafafa]">
			<div className="text-center max-w-full px-6">
				<div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
					<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-gray-400">
						<path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636" />
					</svg>
				</div>
				<h2 className="text-xl font-semibold text-primary mb-2">Unable to access</h2>
				<p className="text-tertiary mb-6">
					Brand not found or you don't have access to view or manage content.
					<br />
					Contact your workspace admin if you believe this is an error.
				</p>
				<button
					onClick={() => navigate("/brands")}
					className="px-4 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer bg-brand text-white hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1"
				>
					Go to Brands
				</button>
			</div>
		</div>
	);
}

/**
 * Guard component that ensures the user is a member of the workspace they're trying to access
 *
 * This guard should be placed after AuthGuard and OnboardingGuard in the route hierarchy
 * to protect workspace-specific routes and ensure users can only access workspaces they belong to.
 *
 * @example
 * <Route element={<AuthGuard />}>
 *   <Route element={<OnboardingGuard />}>
 *     <Route element={<WorkspaceMemberGuard />}>
 *       <Route path="/:brandUrl/dashboard" element={<DashboardPage />} />
 *     </Route>
 *   </Route>
 * </Route>
 */
export default function WorkspaceMemberGuard() {
	const navigate = useNavigate();
	const location = useLocation();
	const params = useParams<{ brandUrl: string }>();
	const { user, currentWorkspaceId, loading: authLoading, updateProfile } = useAuth();
	const { brands, indexBrands } = useBrands();
	const { showLoader, hideLoader } = usePageLoader();
	const syncedWorkspaceRef = useRef<string | null>(null);
	const brandsLoadedRef = useRef(false);
	const accessCheckRef = useRef<string | null>(null);

	const [accessError, setAccessError] = useState<AccessError>(null);

	const brandUrl = params.brandUrl;

	// Check brand access via API
	useEffect(() => {
		const checkBrandAccess = async () => {
			if (!brandUrl || !user || accessCheckRef.current === brandUrl) return;

			accessCheckRef.current = brandUrl;
			setAccessError(null);

			const result = await brandApi.view(brandUrl);

			if (!result.success) {
				if (result.status === 404 || result.status === 403) {
					setAccessError(result.status === 404 ? "not_found" : "access_denied");
				} else {
					console.warn("Error accessing workspace:", result.message);
					navigate("/brands", {
						replace: true,
						state: {
							from: location,
							reason: "workspace_error",
							message: result.message || "Unable to access this workspace",
						},
					});
				}
			}
		};

		if (brandUrl && user && !authLoading) {
			checkBrandAccess();
		}
	}, [brandUrl, user, authLoading, navigate, location]);

	// Load user's accessible brands/workspaces (only once)
	useEffect(() => {
		const loadBrands = async () => {
			if (authLoading || !user || brandsLoadedRef.current) return;

			brandsLoadedRef.current = true;
			await indexBrands();
		};

		if (!brands) {
			loadBrands();
		}
	}, [indexBrands, authLoading, user, brands]);

	const brandsLoading = !brands;

	// Check if user has access to the workspace in the URL (brandUrl)
	const hasAccessToWorkspace = useMemo(() => {
		if (!brandUrl || !brands || !user) {
			return false;
		}

		return brands.some((brand) => brand.id === brandUrl);
	}, [brandUrl, brands, user]);

	// Sync currentWorkspaceId with URL if user has access to the URL workspace
	useEffect(() => {
		if (brandUrl && brandUrl !== currentWorkspaceId && hasAccessToWorkspace && syncedWorkspaceRef.current !== brandUrl) {
			syncedWorkspaceRef.current = brandUrl;
			updateProfile({ current_workspace_id: brandUrl });
		}
	}, [brandUrl, currentWorkspaceId, hasAccessToWorkspace, updateProfile]);

	// Handle loader visibility
	useEffect(() => {
		const shouldShowLoader = authLoading || brandsLoading || (!hasAccessToWorkspace && brands && !accessError);

		if (shouldShowLoader) {
			showLoader();
		} else {
			hideLoader();
		}
	}, [authLoading, brandsLoading, brands, hasAccessToWorkspace, accessError, showLoader, hideLoader]);

	// Check if we're on a workspace-specific route (has brandUrl parameter)
	const isWorkspaceRoute = !!brandUrl;

	// Redirect to brands list if user doesn't have access to the workspace
	useEffect(() => {
		if (authLoading || brandsLoading || !brands) return;
		if (!isWorkspaceRoute) return;

		if (!hasAccessToWorkspace && !accessError) {
			console.warn("User does not have access to workspace:", brandUrl);
			navigate("/brands", {
				replace: true,
				state: {
					from: location,
					reason: "not_workspace_member",
					message: "You don't have access to this workspace",
				},
			});
		}
	}, [authLoading, brandsLoading, brands, isWorkspaceRoute, hasAccessToWorkspace, accessError, brandUrl, navigate, location]);

	// ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS

	// Wait for auth and brands to load
	if (authLoading || brandsLoading || !brands) {
		return null;
	}

	// If not on a workspace route, allow access
	if (!isWorkspaceRoute) {
		return <Outlet />;
	}

	// Show error page for both 404 and 403
	if (accessError) {
		return <WorkspaceErrorDisplay />;
	}

	// If user doesn't have access, show nothing while redirecting
	if (!hasAccessToWorkspace) {
		return null;
	}

	return <Outlet />;
}
